import { Controller } from "@hotwired/stimulus"

/**
 * A row of chips that scrolls sideways on phones (the filters on /travel, /stay, /adventures, /blog, /compare and the
 * leaderboard, the activity chips on an /adventures hub). Only the row moves, never the page.
 * - keeps the current chip (aria-current="page") in view;
 * - fades the edge that has more chips behind it (.has-more right, .has-less left), so a chip cut by the screen
 *   reads as "swipe for more" rather than as a broken layout. The same idea as the "on this page" bar
 *   (trip_nav_controller). Filters that hide chips change the row's width, so it is measured again then.
 *
 * Usage: data-controller="chip-row" on the scrolling element (class chip-row for the fade)
 */
export default class extends Controller<HTMLElement> {
  private observer?: ResizeObserver

  connect() {
    const row = this.element
    const chip = row.querySelector<HTMLElement>("[aria-current='page']")
    if (chip && row.scrollWidth > row.clientWidth) {
      const offset = chip.getBoundingClientRect().left - row.getBoundingClientRect().left
      row.scrollLeft += offset - (row.clientWidth - chip.offsetWidth) / 2
    }

    row.addEventListener("scroll", this.markEdges, { passive: true })
    this.observer = new ResizeObserver(this.markEdges)
    this.observer.observe(row)
    Array.from(row.children).forEach((child) => this.observer?.observe(child))
    this.markEdges()
  }

  disconnect() {
    this.element.removeEventListener("scroll", this.markEdges)
    this.observer?.disconnect()
  }

  private markEdges = () => {
    const row = this.element
    row.classList.toggle("has-less", row.scrollLeft > 4)
    row.classList.toggle("has-more", row.scrollLeft + row.clientWidth < row.scrollWidth - 4)
  }
}
